// Plays the original clips by the original page's rules: an announcer that says "<name>" then the
// event, a commentator that drops a line every 20–60 s, and quiet sound effects. One busy gate
// keeps the voices from talking over each other; sound effects are never gated, only throttled.
// All timing runs from tick(nowMs), so tests can drive it with a fake clock and output.
import type { CatId } from '../race/types';
import { CLIP_LEN, LINES, MIC_ON, NAME_CLIP, type Line } from './clips';

/** One playing clip. */
export interface ClipHandle {
  /** Calls back once when the clip ends (it may never, e.g. when the browser blocks it). */
  onEnded(fn: () => void): void;
  setVolume(v: number): void;
  readonly playing: boolean;
}

/** Where clips actually play. HtmlAudioOut in the browser; a fake in tests. */
export interface ClipOut {
  play(file: string, volume: number): ClipHandle;
  stopAll(): void;
}

const VOICE = 1, SFX = 0.45;
const PAUSE_AFTER_LINE = 1.4, PAUSE_AFTER_CALL = 0.7, STALE_NEWS_MS = 8000, QUEUE_CHECK_MS = 200;
const len = (file: string) => CLIP_LEN[file] ?? 1;

export class ClipPlayer {
  private on = false;
  private master: number;
  private now = 0;
  private busyUntil = 0;
  private pending: { cat: CatId; event: string; at: number }[] = [];
  private nextQueueCheck = 0;
  private lastSfx: Record<string, number> = {};
  private timers: { at: number; fn: () => void }[] = [];
  private nextCommentary = Infinity;
  private playing = new Map<string, { h: ClipHandle; base: number }>();
  /** What played and when (ms), newest last; for the test hook. */
  readonly log: { file: string; at: number; raceT: number }[] = [];
  /** Race clock (ms) of the snapshot being played, stamped on each log entry so cues can be matched to events. */
  raceT = 0;
  /** Race phase for the commentator's choice of pool. */
  running = false;

  constructor(private out: ClipOut, private rand: () => number = Math.random, master = 0.8) {
    this.master = master;
  }

  get soundOn(): boolean { return this.on; }
  get volume(): number { return this.master; }
  busy(): boolean { return this.now < this.busyUntil; }

  /** Sound on: the microphone line, then commentary 16 s later. Sound off: silence everything. */
  setOn(on: boolean): void {
    if (on === this.on) return;
    this.on = on;
    this.busyUntil = 0;
    this.pending.length = 0;
    if (on) {
      this.line(MIC_ON);
      this.nextCommentary = this.now + 16000;
    } else {
      this.out.stopAll();
      this.playing.clear();
      this.timers.length = 0;
      this.nextCommentary = Infinity;
    }
  }

  setVolume(v: number): void {
    this.master = Math.min(1, Math.max(0, v));
    for (const { h, base } of this.playing.values()) if (h.playing) h.setVolume(Math.min(1, base * this.master));
  }

  /** Advance the clock: queued announcer calls, delayed cues and the commentary timer. */
  tick(nowMs: number): void {
    this.now = nowMs;
    const due = this.timers.filter((t) => t.at <= nowMs);
    if (due.length) { this.timers = this.timers.filter((t) => t.at > nowMs); for (const t of due) t.fn(); }
    if (nowMs >= this.nextQueueCheck) {
      this.nextQueueCheck = nowMs + QUEUE_CHECK_MS;
      if (this.on && !this.busy() && this.pending.length) {
        const p = this.pending.shift()!;
        if (nowMs - p.at <= STALE_NEWS_MS) this.speak(p.cat, p.event);
      }
    }
    if (nowMs >= this.nextCommentary) this.commentate();
  }

  /** Run fn after ms (dropped if sound is turned off meanwhile). */
  after(ms: number, fn: () => void): void { if (this.on) this.timers.push({ at: this.now + ms, fn }); }

  /** Announcer: "<cat>" then the event. Waits in a queue while busy; minor calls are dropped instead. */
  announce(cat: CatId, event: string, minor = false): void {
    if (!this.on) return;
    if (this.busy()) { if (!minor) this.pending.push({ cat, event, at: this.now }); return; }
    this.speak(cat, event);
  }

  /** Only the cat's name, and only if nobody is talking (the original's 3rd place). */
  nameOnly(cat: CatId): void {
    if (!this.on || this.busy()) return;
    this.hold(1.2 + PAUSE_AFTER_CALL);
    this.clip(NAME_CLIP[cat], VOICE);
  }

  /** A commentator line, skipped while busy. */
  line(entry: Line): boolean {
    if (!this.on || this.busy()) return false;
    this.hold(entry[0] + PAUSE_AFTER_LINE);
    this.clip(entry[1], VOICE);
    return true;
  }

  /** A quiet sound effect, not replayed within ttl seconds. */
  sfx(file: string, ttl: number): void {
    if (!this.on) return;
    if (this.now - (this.lastSfx[file] ?? -Infinity) < ttl * 1000) return;
    this.lastSfx[file] = this.now;
    this.clip(file, SFX);
  }

  /** Files that are playing now. */
  current(): string[] { return [...this.playing].filter(([, p]) => p.h.playing).map(([f]) => f); }

  private hold(seconds: number): void { this.busyUntil = Math.max(this.busyUntil, this.now + seconds * 1000); }

  private speak(cat: CatId, event: string): void {
    const name = NAME_CLIP[cat];
    const [first, second] = event === '5thplace.mp3' ? [event, name] : [name, event];
    this.hold(len(first) + len(second) + PAUSE_AFTER_CALL);
    let chained = false;
    const next = () => { if (chained || !this.on) return; chained = true; this.clip(second, VOICE); };
    this.clip(first, VOICE).onEnded(next);
    this.timers.push({ at: this.now + (len(first) + 0.15) * 1000, fn: next }); // if 'ended' never fires
  }

  private commentate(): void {
    this.nextCommentary = this.now + 20000 + this.rand() * 40000;
    const pick = (pool: readonly Line[]) => pool[Math.floor(this.rand() * pool.length)]!;
    if (this.rand() < 0.1) {
      if (this.rand() < 0.3 && this.line(pick(LINES.prejoke))) this.after(5100, () => this.line(pick(LINES.joke)));
      else this.line(pick(LINES.joke));
    } else this.line(pick(this.running ? LINES.running : LINES.notrunning));
  }

  private clip(file: string, base: number): ClipHandle {
    const h = this.out.play(file, Math.min(1, base * this.master));
    this.playing.set(file, { h, base });
    this.log.push({ file, at: Math.round(this.now), raceT: Math.round(this.raceT) });
    if (this.log.length > 200) this.log.shift(); // a whole race
    return h;
  }
}

/** Browser output: one reusable <audio> element per file, as the original does. */
export class HtmlAudioOut implements ClipOut {
  private els = new Map<string, HTMLAudioElement>();
  constructor(private url: (file: string) => string) {}

  play(file: string, volume: number): ClipHandle {
    let a = this.els.get(file);
    if (!a) { a = new Audio(this.url(file)); a.preload = 'auto'; this.els.set(file, a); }
    const el = a;
    el.volume = volume;
    try { el.currentTime = 0; void el.play()?.catch(() => {}); } catch { /* autoplay refused */ }
    return {
      onEnded: (fn) => el.addEventListener('ended', fn, { once: true }),
      setVolume: (v) => { el.volume = v; },
      get playing() { return !el.paused && !el.ended; },
    };
  }

  stopAll(): void { for (const a of this.els.values()) { try { a.pause(); } catch { /* ignore */ } } }
}
